<script setup lang="ts">
import { onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import {
  plus_small,
  check_circle,
  edit,
  trash_can,
} from "pn-design-assets/pn-assets/icons";
import { userStickers } from "@/stores/stickers";
import { useImageStore } from "@/stores/images";
import { useRouter } from "vue-router";
import ConfirmationModal from "../modals/ConfirmationModal.vue";
import { useTagManagementStore } from "@/stores/tagManagement";
import type { StickerCollection } from "@/types/stickers";

const stickerStore = userStickers();
const showSpinner = ref(false);
const { notificationObj } = storeToRefs(stickerStore);
const router = useRouter();
const imageStore = useImageStore();
const tagManagementStore = useTagManagementStore();
const showDeleteConfirmationModal = ref(false);
const selectedCollectionId = ref<number | null>(null);
onMounted(async () => {
  try {
    if (!notificationObj.value.show && !notificationObj.value.message) {
      stickerStore.resetNotification();
    }
    showSpinner.value = true;
    tagManagementStore.getTagTree();
    await stickerStore.getAllStickerCollections();
    await imageStore.getImageCategories();
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      status: "error",
      message: "There was an error fetching sticker collections",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
    if (notificationObj.value.message && !notificationObj.value.show) {
      notificationObj.value.show = true;
      setTimeout(() => {
        stickerStore.resetNotification();
      }, 4000);
    }
  }
});

const goToCreateCollection = () => {
  router.push("/create-sticker-collection");
};

const getStatusTagLabel = (isCollectionActive: boolean) => {
  return isCollectionActive ? "Active" : "Inactive";
};

const getStatusTagAppearance = (isCollectionActive: boolean) => {
  return isCollectionActive ? "positive" : "error";
};

const editStickerCollection = (collectionId: number) => {
  router.push(`/sticker-collection/${collectionId}`);
};

const openDeleteConfirmationModal = (collectionId: number) => {
  selectedCollectionId.value = collectionId;
  showDeleteConfirmationModal.value = true;
};

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
  selectedCollectionId.value = null;
};

const deleteStickerCollection = async (collectionId: number) => {
  try {
    stickerStore.resetNotification();
    showSpinner.value = true;
    await stickerStore.deleteStickerCollection(collectionId);
    await stickerStore.getAllStickerCollections();
    stickerStore.populateNotificationObj({
      show: true,
      status: "success",
      message: "Sticker collection deleted successfully",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      status: "error",
      message: "There was an error deleting sticker collection",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
  }
};

const confirmDeleteStickerCollection = async () => {
  if (selectedCollectionId.value === null) {
    closeDeleteConfirmationModal();
    return;
  }
  await deleteStickerCollection(selectedCollectionId.value);
  closeDeleteConfirmationModal();
};

const getAvailableStickerCount = (collection: StickerCollection) => {
  return (
    Number(collection.stickerCount) -
    Number(collection.incompleteStickerCount ?? 0)
  );
};
</script>

<template>
  <div class="header-wrapper">
    <pn-toast
      class="bottom-toast"
      :appearance="notificationObj.status"
      v-show="notificationObj.show"
      :heading="notificationObj.heading"
      :text="notificationObj.message"
      temporary
    ></pn-toast>
    <div class="header">
      <div class="header-left">
        <h2 class="pn-xl margin-0">Stickers</h2>
        <p class="header-preamble">
          Create new collections of stickers or upload to already existing
          collections.
        </p>
      </div>
      <pn-button
        left-icon="true"
        :icon="plus_small"
        @pnClick="goToCreateCollection"
        >Create collection</pn-button
      >
    </div>
  </div>
  <section class="flex flex-direction-column align-items-start gap-16">
    <div :class="[{ spinner: showSpinner }]">
      <pn-spinner size="3" v-show="showSpinner" />
    </div>
    <div class="flex align-self-stretch align-end gap-10">
      <div class="description">
        <h2 class="pn-l">Collections</h2>
        <!--<p class="margin-0 pn-s">TBD</p> -->
      </div>
      <!-- <pn-button
        variant="borderless"
        appearance="light"
        :icon="angle_small_up"
        class="order-collection-btn"
        >Order Collection</pn-button
      > -->
    </div>
  </section>
  <section class="stickers">
    <div
      class="sticker-card"
      v-for="collection in stickerStore.getOrderedStickerCOllection"
      :key="collection.collectionId"
    >
      <pn-card>
        <div class="flex justify-space-between">
          <div class="desc-count">
            <p class="text-blue font-weight-medium collection-name">
              {{ collection.enname }}
            </p>
            <div class="flex gap-4 count-row">
              <span class="text-blue count pn-s">{{
                getAvailableStickerCount(collection)
              }}</span>
              <span
                class="critical pn-s incomplete-text"
                v-if="(collection.incompleteStickerCount ?? 0) > 0"
                >( {{ collection.incompleteStickerCount }} incompleted)</span
              >
            </div>
          </div>
          <div class="status">
            <pn-tag
              small
              :label="getStatusTagLabel(collection.active)"
              :appearance="getStatusTagAppearance(collection.active)"
              :icon="check_circle"
            ></pn-tag>
          </div>
        </div>
        <div
          class="thumbs-grid"
          :class="{ 'col-inactive': !collection.active }"
        >
          <img
            v-for="(stickerImage, index) in collection.thumbnails"
            class="thumb"
            :key="`sticketIamge-${index}`"
            :src="stickerImage"
          />
        </div>
        <div class="flex justify-flex-end gap-10">
          <pn-button
            variant="outlined"
            appearance="light"
            :icon="edit"
            icon-only
            tooltip="Edit"
            @pnClick="editStickerCollection(collection.collectionId)"
          />
          <pn-button
            variant="outlined"
            appearance="light"
            :icon="trash_can"
            icon-only
            tooltip="Delete"
            @pnClick="openDeleteConfirmationModal(collection.collectionId)"
          />
        </div>
      </pn-card>
    </div>
  </section>
  <ConfirmationModal
    :open="showDeleteConfirmationModal"
    :showLoader="showSpinner"
    heading="Are you sure you want to delete this collection?"
    @onCancel="closeDeleteConfirmationModal"
    @onConfirm="confirmDeleteStickerCollection"
  />
</template>

<style lang="scss" scoped>
.header {
  display: flex;
  padding: 0;
  align-items: center;
  gap: 16px;
  align-self: stretch;
}

.header-left {
  display: flex;
  padding: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
}

.header-right {
  display: flex;
  padding: var(--Space-null, 0);
  align-items: center;
  gap: var(--Space-xl, 16px);
  align-self: stretch;
}

.header-preamble {
  color: #767676;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0;
  margin: 0;
}

.header-wrapper {
  display: flex;
  width: 100%;
  max-width: 1064px;
  min-width: 600px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 16px;
}

.gap-16 {
  gap: 16px;
}

.gap-10 {
  gap: 10px;
}

.description {
  display: flex;
  padding-top: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
}

.stickers {
  display: grid;
  width: 100%;
  max-width: 1004px;
  gap: 16px;
  grid-template-columns: repeat(3, 1fr);
}

.sticker-card {
  border-radius: 8px;
  background: #fff;
  /* allow this grid item to shrink below its nowrap content's intrinsic width */
  min-width: 0;
}

.sticker-card pn-card {
  height: 100%;
}

.desc-count {
  /* allow this flex item to shrink below its content's intrinsic width */
  min-width: 0;
}

.collection-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.count-row {
  min-width: 0;
}

.incomplete-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.thumbs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: #f8f8f8;
  padding: 16px 8px 16px 8px;
  min-height: 166px;
}

.thumb {
  width: 100%;
  max-height: 60px;
}

.label {
  font-size: 16px;
  font-weight: 500;
  color: #005d92;
  line-height: 18px;
}

.count {
  font-weight: 400 !important;
}

.order-collection-btn {
  margin-right: 2rem;
}

.col-inactive {
  position: relative;
}
.col-inactive:after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: #f8f8f8;
  left: 0;
  top: 0;
  opacity: 0.5;
}

.bottom-toast {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  max-width: calc(100vw - 32px);
  text-align: center;
  z-index: 1000;
}
</style>
